/**
 * Publish an owned prompt / pack / project to the public library.
 *
 * All defaults come from the source entity; the user may override title,
 * description, and tags, and opt to include the full PAIQ analysis.
 */
import { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { libraryApi, type LibraryItemType } from '../services/api';
import categoriesData from '../../prompt-categories.json';

interface PromptCategory {
  id: number;
  code: string;
  slug: string;
  name: string;
  description: string;
  tags: string[];
}

const CATEGORIES = (categoriesData.categories as PromptCategory[]).slice().sort((a, b) =>
  a.name.localeCompare(b.name),
);

interface PublishToLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  type: LibraryItemType;
  sourceId: string;
  defaultTitle: string;
  defaultDescription?: string;
  defaultTags?: string[];
  /** Show the "include PAIQ analysis" toggle (only meaningful for scored prompts). */
  showPaiqOption?: boolean;
  onPublished?: (result: {
    code: string;
    landingUrl: string;
    itemsPublished?: number;
    items?: Array<{ code: string; landingUrl: string; title: string; updated: boolean }>;
  }) => void;
}

export default function PublishToLibraryDialog({
  open,
  onClose,
  type,
  sourceId,
  defaultTitle,
  defaultDescription,
  defaultTags,
  showPaiqOption,
  onPublished,
}: PublishToLibraryDialogProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription ?? '');
  const [tags, setTags] = useState((defaultTags ?? []).join(', '));
  const [category, setCategory] = useState<PromptCategory | null>(null);
  const [includePaiq, setIncludePaiq] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    code: string;
    landingUrl: string;
    updated: boolean;
    itemsPublished?: number;
    items?: Array<{ code: string; landingUrl: string; title: string; updated: boolean }>;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle);
      setDescription(defaultDescription ?? '');
      setTags((defaultTags ?? []).join(', '));
      setCategory(null);
      setIncludePaiq(false);
      setError(null);
      setResult(null);
      setCopied(false);
    }
  }, [open, defaultTitle, defaultDescription, defaultTags]);

  const handleCategoryChange = (_event: unknown, value: PromptCategory | null) => {
    setCategory(value);
    // Assigning a category populates its associated tags (from prompt-categories.json).
    if (value) {
      setTags(value.tags.join(', '));
    }
  };

  const handlePublish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await libraryApi.publish({
        type,
        sourceId,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        category: category?.name,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        includePaiqAnalysis: includePaiq,
      });
      setResult(res.data);
      onPublished?.(res.data);
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? (err instanceof Error ? err.message : 'Failed to publish');
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.landingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Publish to Public Library</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Stack spacing={2}>
            {result.items && result.items.length > 0 ? (
              <>
                <Alert severity="success">
                  {result.updated
                    ? `Refreshed ${result.itemsPublished ?? result.items.length} prompt${
                        (result.itemsPublished ?? result.items.length) === 1 ? '' : 's'
                      } in the public library — one entry per prompt.`
                    : `Published ${result.itemsPublished ?? result.items.length} prompt${
                        (result.itemsPublished ?? result.items.length) === 1 ? '' : 's'
                      } to the public library — one entry per prompt.`}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Each prompt now has its own searchable library entry. Titles start with the
                  project and chapter so related prompts group together.
                </Typography>
                <Stack
                  spacing={1}
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {result.items.map((it) => (
                    <Stack
                      key={it.code}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Link
                        href={it.landingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                        title={it.title}
                      >
                        {it.title}
                      </Link>
                      <IconButton
                        size="small"
                        onClick={() => {
                          void navigator.clipboard.writeText(it.landingUrl);
                        }}
                        title="Copy shareable link"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : (
              <>
                <Alert severity="success">
                  {result.updated
                    ? `Updated! Your existing ${type} in the public library has been refreshed.`
                    : `Published! Your ${type} is now discoverable in the public library.`}
                </Alert>
                <TextField
                  label="Shareable link"
                  value={result.landingUrl}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopy} edge="end" size="small">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText={copied ? 'Copied to clipboard!' : ' '}
                />
                <Typography variant="body2" color="text.secondary">
                  Library code:{' '}
                  <Link href={result.landingUrl} target="_blank" rel="noopener noreferrer">
                    {result.code}
                  </Link>
                </Typography>
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {type === 'project' && (
              <Alert severity="info">
                Each prompt in this project will be published as its own library entry. Titles are
                composed automatically as{' '}
                <em>&lt;book title&gt; · &lt;chapter title&gt; · &lt;prompt title&gt;</em> so
                related prompts group together.
              </Alert>
            )}
            {type !== 'project' && (
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                disabled={busy}
              />
            )}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={busy}
            />
            <Autocomplete
              options={CATEGORIES}
              value={category}
              onChange={handleCategoryChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, val) => option.code === val.code}
              disabled={busy}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  required
                  helperText="Selecting a category fills in its associated tags below."
                />
              )}
            />
            <TextField
              label="Tags (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              fullWidth
              disabled={busy}
            />
            {showPaiqOption && (
              <FormControlLabel
                control={
                  <Switch
                    checked={includePaiq}
                    onChange={(e) => setIncludePaiq(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Include PAIQ analysis in the published item"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              Publishing shares this content publicly. You can unpublish it at any time from the
              Library page.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              variant="contained"
              disabled={busy || !title.trim() || !category}
            >
              {busy ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
